/**
 * Shopify Integration OAuth Utilities (Story 12.4, Task 1)
 *
 * Handles Shopify OAuth URL building, token exchange, HMAC validation, and store URL normalization.
 *
 * Key differences from Mailchimp/Meta/LinkedIn/TikTok:
 * - Store URL pre-input required (auth URL is store-specific)
 * - HMAC callback validation using raw query string (unique to Shopify)
 * - No grant_options[] — omitting defaults to offline access (permanent tokens)
 * - Token exchange: application/x-www-form-urlencoded (NOT JSON)
 * - No refresh tokens — offline access tokens are permanent
 * - Single account per token (1 store = 1 token)
 * - SSRF defense: validate hostname ends in .myshopify.com before any POST
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const TOKEN_EXCHANGE_TIMEOUT_MS = 25_000;
const SHOP_INFO_TIMEOUT_MS = 10_000;
const SHOPIFY_API_VERSION = "2026-04"; // Must match shopify.ts — mandatory expiring tokens in 2026-04

/** Regex for valid normalized Shopify store domains */
const SHOP_DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;

/** Maximum allowed clock skew for callback timestamp validation (5 minutes) */
const MAX_TIMESTAMP_SKEW_SECONDS = 300;

/** Scopes required for ShopifyQL analytics + product data */
const SHOPIFY_SCOPES = "read_orders,read_products,read_customers,read_reports";

// ============================================================================
// Types
// ============================================================================

export interface ShopifyAuthUrlOptions {
  shop: string;
  clientId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
}

export interface ShopifyTokenResponse {
  access_token: string;
  scope: string;
}

export interface ShopifyShopInfo {
  name: string;
  myshopifyDomain: string;
  currencyCode: string;
  timezoneAbbreviation: string;
}

export interface ShopifyCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "none";
}

// ============================================================================
// Store URL Normalization & Validation (Task 1.2)
// ============================================================================

/**
 * Normalizes and validates a Shopify store URL/domain input.
 *
 * Accepts various input formats:
 * - "mystore" -> "mystore.myshopify.com"
 * - "mystore.myshopify.com" -> "mystore.myshopify.com"
 * - "https://mystore.myshopify.com" -> "mystore.myshopify.com"
 * - "https://mystore.myshopify.com/admin" -> "mystore.myshopify.com"
 *
 * @returns Normalized shop domain or null if invalid
 */
export function validateShopDomain(input: string): string | null {
  if (!input || typeof input !== "string") return null;

  let normalized = input.trim().toLowerCase();

  // Strip protocol prefixes
  normalized = normalized.replace(/^https?:\/\//, "");

  // Strip trailing slash and any path
  const slashIndex = normalized.indexOf("/");
  if (slashIndex !== -1) {
    normalized = normalized.slice(0, slashIndex);
  }

  // Strip .myshopify.com suffix if present, then re-add it
  // This handles "mystore.myshopify.com" and "mystore" uniformly
  if (normalized.endsWith(".myshopify.com")) {
    normalized = normalized.slice(0, -".myshopify.com".length);
  }

  // Append the standard suffix
  normalized = `${normalized}.myshopify.com`;

  // Validate against regex
  if (!SHOP_DOMAIN_REGEX.test(normalized)) {
    return null;
  }

  return normalized;
}

// ============================================================================
// Build Authorization URL (Task 1.1)
// ============================================================================

/**
 * Builds the Shopify OAuth 2.0 authorization URL.
 *
 * CRITICAL: Does NOT include grant_options[]. Omitting defaults to offline access
 * (permanent tokens). Including grant_options[]=per-user would yield 24h online
 * tokens, breaking the architecture.
 */
export function buildShopifyAuthUrl(options: ShopifyAuthUrlOptions): string {
  const { shop, clientId, redirectUri, stateKey } = options;

  if (!shop) {
    throw new Error("Shop domain is required");
  }
  if (!clientId) {
    throw new Error("SHOPIFY_CLIENT_ID is required");
  }
  if (!redirectUri) {
    throw new Error("SHOPIFY_OAUTH_REDIRECT_URI is required");
  }
  if (!stateKey) {
    throw new Error("stateKey is required for CSRF protection");
  }

  // Validate shop domain (SSRF defense)
  if (!SHOP_DOMAIN_REGEX.test(shop)) {
    throw new Error("Invalid shop domain");
  }

  const url = new URL(`https://${shop}/admin/oauth/authorize`);
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("scope", SHOPIFY_SCOPES);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  // NOTE: No grant_options[] — offline access is the default

  return url.toString();
}

// ============================================================================
// HMAC Callback Validation (Task 1.3)
// ============================================================================

/**
 * Verifies the HMAC signature on a Shopify OAuth callback.
 *
 * CRITICAL: Uses RAW query string, NOT URLSearchParams (which auto-decodes
 * values and would change the HMAC input).
 *
 * Uses Web Crypto API (crypto.subtle) since Cloudflare Workers doesn't
 * support Node's crypto.createHmac().
 *
 * @param rawQueryString - Everything after `?` in the callback URL
 * @param clientSecret - Shopify app client secret
 * @returns true if HMAC is valid
 */
export async function verifyShopifyHmac(
  rawQueryString: string,
  clientSecret: string
): Promise<boolean> {
  if (!rawQueryString || !clientSecret) return false;

  // Split on &, extract hmac value, remove hmac pair
  const pairs = rawQueryString.split("&");
  let providedHmac = "";
  const filteredPairs: string[] = [];

  for (const pair of pairs) {
    if (pair.startsWith("hmac=")) {
      providedHmac = pair.slice(5); // "hmac=".length === 5
    } else {
      filteredPairs.push(pair);
    }
  }

  if (!providedHmac) return false;

  // Sort remaining pairs lexicographically
  filteredPairs.sort();
  const message = filteredPairs.join("&");

  // Compute HMAC-SHA256 using Web Crypto API
  const encoder = new TextEncoder();
  const keyData = encoder.encode(clientSecret);
  const messageData = encoder.encode(message);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const signatureBuffer = await crypto.subtle.sign("HMAC", cryptoKey, messageData);
  const signatureArray = new Uint8Array(signatureBuffer);

  // Convert to hex
  const computedHmac = Array.from(signatureArray)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Constant-time comparison
  return timingSafeEqual(computedHmac, providedHmac);
}

/**
 * Validates that the callback timestamp is within acceptable skew.
 *
 * @param timestamp - Unix timestamp string from callback
 * @returns true if timestamp is within 5 minutes of server time
 */
export function validateCallbackTimestamp(timestamp: string): boolean {
  const callbackTime = parseInt(timestamp, 10);
  if (isNaN(callbackTime)) return false;

  const serverTime = Math.floor(Date.now() / 1000);
  return Math.abs(serverTime - callbackTime) <= MAX_TIMESTAMP_SKEW_SECONDS;
}

// ============================================================================
// Timing-Safe Comparison (replicated from packages/auth)
// ============================================================================

/**
 * Constant-time string comparison to prevent timing attacks.
 * Replicated from packages/auth/src/session/refresh.ts.
 */
function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;

  const encoder = new TextEncoder();
  const bufA = encoder.encode(a);
  const bufB = encoder.encode(b);

  let result = 0;
  for (let i = 0; i < bufA.length; i++) {
    result |= (bufA[i] as number) ^ (bufB[i] as number);
  }

  return result === 0;
}

// ============================================================================
// Token Exchange (Task 1.4)
// ============================================================================

/**
 * Exchanges an authorization code for an offline access token.
 *
 * CRITICAL: Uses application/x-www-form-urlencoded (NOT JSON).
 * SSRF defense: Validates hostname ends in .myshopify.com before POST.
 *
 * Shopify offline access tokens are permanent — no expires_in, no refresh_token.
 */
export async function exchangeShopifyCode(
  shop: string,
  code: string,
  clientId: string,
  clientSecret: string
): Promise<ShopifyTokenResponse> {
  // SSRF defense: validate hostname
  const tokenUrl = new URL(`https://${shop}/admin/oauth/access_token`);
  if (!tokenUrl.hostname.endsWith(".myshopify.com")) {
    throw new Error("Invalid shop hostname for token exchange");
  }

  const body = new URLSearchParams({
    client_id: clientId,
    client_secret: clientSecret,
    code,
  });

  const result = await safeFetch(
    tokenUrl.toString(),
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    throw new Error(
      sanitizeErrorMessage(`Shopify token exchange failed: ${result.error.message}`)
    );
  }

  const response = result.data;
  if (!response.ok) {
    let errorMessage = `Shopify token exchange failed: HTTP ${response.status}`;
    try {
      const errorBody = (await response.json()) as {
        error?: string;
        error_description?: string;
      };
      if (errorBody.error_description) {
        errorMessage = `Shopify token exchange failed: ${errorBody.error_description}`;
      } else if (errorBody.error) {
        errorMessage = `Shopify token exchange failed: ${errorBody.error}`;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(sanitizeErrorMessage(errorMessage));
  }

  const data = (await response.json()) as ShopifyTokenResponse;

  if (!data.access_token) {
    throw new Error("Invalid Shopify token response: missing access_token");
  }

  return data;
}

// ============================================================================
// Shop Info Fetch (Task 1.5)
// ============================================================================

/**
 * Fetches shop information via GraphQL after token exchange.
 *
 * Used to get the store name, domain, currency, and timezone.
 */
export async function fetchShopInfo(
  shop: string,
  accessToken: string
): Promise<ShopifyShopInfo> {
  // SSRF defense
  const graphqlUrl = new URL(
    `https://${shop}/admin/api/${SHOPIFY_API_VERSION}/graphql.json`
  );
  if (!graphqlUrl.hostname.endsWith(".myshopify.com")) {
    throw new Error("Invalid shop hostname for shop info fetch");
  }

  const query = `{ shop { name myshopifyDomain currencyCode timezoneAbbreviation } }`;

  const result = await safeFetch(
    graphqlUrl.toString(),
    {
      method: "POST",
      headers: {
        "X-Shopify-Access-Token": accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query }),
    },
    SHOP_INFO_TIMEOUT_MS
  );

  if (!result.success) {
    throw new Error(
      sanitizeErrorMessage(`Shopify shop info fetch failed: ${result.error.message}`)
    );
  }

  const response = result.data;
  if (!response.ok) {
    throw new Error(
      sanitizeErrorMessage(`Shopify shop info fetch failed: HTTP ${response.status}`)
    );
  }

  const body = (await response.json()) as {
    data?: { shop?: ShopifyShopInfo };
    errors?: Array<{ message: string }>;
  };

  if (body.errors && body.errors.length > 0) {
    throw new Error(
      sanitizeErrorMessage(
        `Shopify shop info GraphQL error: ${body.errors[0]?.message ?? "Unknown error"}`
      )
    );
  }

  if (!body.data?.shop) {
    throw new Error("Invalid Shopify shop info response: missing shop data");
  }

  return body.data.shop;
}

// ============================================================================
// Error Parsing
// ============================================================================

/**
 * Parses Shopify OAuth callback errors from query params.
 *
 * Shopify returns errors via the `error` query parameter on redirect.
 */
export function parseShopifyCallbackError(
  searchParams: URLSearchParams
): ShopifyCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage: "You denied the Shopify connection request.",
      userAction: "retry",
    };
  }

  if (error === "invalid_request") {
    return {
      code: "oauth-config-error",
      userMessage: "Shopify connection is misconfigured. Contact support.",
      userAction: "none",
    };
  }

  return {
    code: "oauth-error",
    userMessage: "An error occurred connecting to Shopify. Please try again.",
    userAction: "retry",
  };
}

// ============================================================================
// Exports for testing
// ============================================================================

export { SHOPIFY_API_VERSION, SHOP_DOMAIN_REGEX, SHOPIFY_SCOPES };
